import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { PageHeader, PageBody, fmtMoney } from "@/components/PageHeader";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Printer } from "@phosphor-icons/react";

export default function ReportsPage() {
  const { activeCompany } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("tb");
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [tb, setTb] = useState(null);
  const [pl, setPl] = useState(null);
  const [bs, setBs] = useState(null);
  const [sbs, setSbs] = useState(null);

  const run = async () => {
    if (!activeCompany) return;
    const q = new URLSearchParams();
    if (start) q.set("start", start);
    if (end) q.set("end", end);
    const [t, p, b, s] = await Promise.all([
      api.get(`/companies/${activeCompany.id}/reports/trial-balance?${q}`),
      api.get(`/companies/${activeCompany.id}/reports/profit-loss?${q}`),
      api.get(`/companies/${activeCompany.id}/reports/balance-sheet?${end ? "end=" + end : ""}`),
      api.get(`/companies/${activeCompany.id}/reports/sales-by-salesman?${q}`),
    ]);
    setTb(t.data);
    setPl(p.data);
    setBs(b.data);
    setSbs(s.data);
  };

  useEffect(() => {
    run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeCompany?.id]);

  if (!activeCompany) return <PageBody><div className="overline">Select a company</div></PageBody>;

  const drill = (accountId) => {
    if (!accountId) return;
    const q = new URLSearchParams({ account: accountId });
    if (start) q.set("start", start);
    if (end) q.set("end", end);
    navigate(`/reports/gl-detail?${q.toString()}`);
  };

  return (
    <>
      <PageHeader
        eyebrow="Analytics"
        title="Financial Reports"
        description="Trial balance, profit & loss, and balance sheet. Filter by date range."
        actions={
          <Button
            variant="outline"
            size="sm"
            onClick={() => window.print()}
            data-testid="reports-print-btn"
            className="rounded-sm font-mono uppercase tracking-wider text-xs"
          >
            <Printer size={14} className="mr-1" /> Print / PDF
          </Button>
        }
      />
      <PageBody>
        <div className="report-filter-bar border border-border bg-card p-6 grid grid-cols-1 md:grid-cols-4 gap-4 items-end">
          <div>
            <Label className="text-xs uppercase tracking-widest">Start</Label>
            <Input
              type="date"
              value={start}
              onChange={(e) => setStart(e.target.value)}
              data-testid="report-start"
              className="rounded-sm font-mono"
            />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest">End</Label>
            <Input
              type="date"
              value={end}
              onChange={(e) => setEnd(e.target.value)}
              data-testid="report-end"
              className="rounded-sm font-mono"
            />
          </div>
          <Button
            onClick={run}
            data-testid="run-report"
            className="rounded-sm h-10 font-mono uppercase tracking-wider text-xs md:col-span-1"
          >
            Run Report
          </Button>
        </div>

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="rounded-sm bg-muted">
            <TabsTrigger value="tb" data-testid="tab-tb" className="rounded-sm font-mono uppercase text-xs">
              Trial Balance
            </TabsTrigger>
            <TabsTrigger value="pl" data-testid="tab-pl" className="rounded-sm font-mono uppercase text-xs">
              Profit & Loss
            </TabsTrigger>
            <TabsTrigger value="bs" data-testid="tab-bs" className="rounded-sm font-mono uppercase text-xs">
              Balance Sheet
            </TabsTrigger>
            <TabsTrigger value="sbs" data-testid="tab-sbs" className="rounded-sm font-mono uppercase text-xs">
              Sales by Salesman
            </TabsTrigger>
          </TabsList>

          <TabsContent value="tb" className="mt-4">
            <PrintHeader company={activeCompany} title="Trial Balance" start={start} end={end} />
            {tb && <TrialBalance data={tb} currency={activeCompany.currency} onDrill={drill} />}
          </TabsContent>
          <TabsContent value="pl" className="mt-4">
            <PrintHeader company={activeCompany} title="Profit & Loss" start={start} end={end} />
            {pl && <ProfitLoss data={pl} currency={activeCompany.currency} onDrill={drill} />}
          </TabsContent>
          <TabsContent value="bs" className="mt-4">
            <PrintHeader company={activeCompany} title="Balance Sheet" start={start} end={end} />
            {bs && <BalanceSheet data={bs} currency={activeCompany.currency} onDrill={drill} />}
          </TabsContent>
          <TabsContent value="sbs" className="mt-4">
            <PrintHeader company={activeCompany} title="Sales by Salesman" start={start} end={end} />
            {sbs && <SalesBySalesman data={sbs} currency={activeCompany.currency} />}
          </TabsContent>
        </Tabs>
      </PageBody>
    </>
  );
}

export function TrialBalance({ data, currency, onDrill }) {
  return (
    <div className="border border-border bg-card">
      <table className="w-full text-sm">
        <thead className="border-b border-border bg-muted/40">
          <tr className="text-left">
            <Th>Code</Th>
            <Th>Account</Th>
            <Th>Type</Th>
            <Th className="text-right">Debit</Th>
            <Th className="text-right">Credit</Th>
            <Th className="text-right pr-4">Balance</Th>
          </tr>
        </thead>
        <tbody>
          {data.rows.map((r) => (
            <tr
              key={(r.account_id || r.code) + r.name}
              data-testid={`tb-row-${r.code}`}
              className={`border-b border-border last:border-b-0 ${
                r.account_id && onDrill
                  ? "cursor-pointer hover:bg-primary/5 transition-colors"
                  : ""
              }`}
              onClick={() => r.account_id && onDrill && onDrill(r.account_id)}
              title={r.account_id ? "Drill down into this GL" : ""}
            >
              <Td className="num">{r.code}</Td>
              <Td>
                {r.name}
                {r.account_id && (
                  <span className="drill-hint ml-2 text-[10px] uppercase tracking-widest text-primary opacity-60">
                    Drill →
                  </span>
                )}
              </Td>
              <Td><span className="tag-badge capitalize">{r.type}</span></Td>
              <Td className="text-right num">{r.debit ? fmtMoney(r.debit, currency) : "—"}</Td>
              <Td className="text-right num">{r.credit ? fmtMoney(r.credit, currency) : "—"}</Td>
              <Td className="text-right num pr-4">{fmtMoney(r.balance, currency)}</Td>
            </tr>
          ))}
          <tr className="bg-muted/60 border-t-2 border-primary">
            <Td colSpan={3} className="font-serif-display text-lg">Totals</Td>
            <Td className="text-right num font-medium">{fmtMoney(data.total_debit, currency)}</Td>
            <Td className="text-right num font-medium">{fmtMoney(data.total_credit, currency)}</Td>
            <Td className="pr-4"></Td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}

export function ProfitLoss({ data, currency, onDrill }) {
  return (
    <div className="border border-border bg-card p-6 space-y-6">
      <Section title="Revenue" items={data.revenues} total={data.total_revenue} currency={currency} onDrill={onDrill} />
      <Section title="Expenses" items={data.expenses} total={data.total_expense} currency={currency} onDrill={onDrill} />
      <div className="border-t-2 border-primary pt-4 flex items-center justify-between">
        <div className="font-serif-display text-2xl">Net Income</div>
        <div className="font-serif-display text-2xl num">
          {fmtMoney(data.net_income, currency)}
        </div>
      </div>
    </div>
  );
}

export function BalanceSheet({ data, currency, onDrill }) {  return (
    <div className="border border-border bg-card p-6 grid grid-cols-1 md:grid-cols-2 gap-8">
      <div>
        <Section title="Assets" items={data.assets} total={data.total_assets} currency={currency} onDrill={onDrill} />
      </div>
      <div className="space-y-6">
        <Section title="Liabilities" items={data.liabilities} total={data.total_liabilities} currency={currency} onDrill={onDrill} />
        <Section title="Equity" items={data.equity} total={data.total_equity} currency={currency} onDrill={onDrill} />
        <div className="border-t border-border pt-3 flex items-center justify-between">
          <div className="overline">Retained (Net Income)</div>
          <div className="num">{fmtMoney(data.net_income, currency)}</div>
        </div>
        <div className="border-t-2 border-primary pt-4 flex items-center justify-between">
          <div className="font-serif-display text-xl">Total L + E</div>
          <div className="font-serif-display text-xl num">
            {fmtMoney(data.total_liab_equity, currency)}
          </div>
        </div>
      </div>
    </div>
  );
}

function Section({ title, items, total, currency, onDrill }) {
  return (
    <div>
      <div className="overline mb-2">{title}</div>
      <div className="border border-border">
        {items.length === 0 && (
          <div className="p-3 text-sm text-muted-foreground">No entries.</div>
        )}
        {items.map((r) => (
          <div
            key={(r.account_id || r.code) + r.name}
            data-testid={`section-row-${r.code}`}
            className={`grid grid-cols-12 gap-2 px-3 py-2 border-b border-border last:border-b-0 text-sm ${
              r.account_id && onDrill
                ? "cursor-pointer hover:bg-primary/5 transition-colors"
                : ""
            }`}
            onClick={() => r.account_id && onDrill && onDrill(r.account_id)}
            title={r.account_id ? "Drill down into this GL" : ""}
          >
            <div className="col-span-2 num">{r.code}</div>
            <div className="col-span-7">
              {r.name}
              {r.account_id && onDrill && (
                <span className="drill-hint ml-2 text-[10px] uppercase tracking-widest text-primary opacity-60">
                  Drill →
                </span>
              )}
            </div>
            <div className="col-span-3 text-right num">{fmtMoney(r.amount ?? r.balance, currency)}</div>
          </div>
        ))}
        <div className="grid grid-cols-12 px-3 py-2 bg-muted/60 border-t-2 border-primary">
          <div className="col-span-9 font-serif-display text-lg">Total {title}</div>
          <div className="col-span-3 text-right font-serif-display text-lg num">
            {fmtMoney(total, currency)}
          </div>
        </div>
      </div>
    </div>
  );
}

function PrintHeader({ company, title, start, end }) {
  const generated = new Date().toLocaleString();
  const period = start || end
    ? `${start || "beginning"} → ${end || "today"}`
    : "All periods";
  return (
    <div className="hidden print:block mb-6 border-b-2 border-primary pb-3">
      <div className="flex items-start justify-between">
        <div>
          <div className="text-xs uppercase tracking-widest text-muted-foreground">
            LedgerLoom · Precision Bookkeeping
          </div>
          <div className="font-serif-display text-3xl mt-1">{company?.name}</div>
          <div className="text-sm text-muted-foreground mt-1">{title}</div>
        </div>
        <div className="text-right text-xs text-muted-foreground">
          <div>Period: {period}</div>
          <div>Generated: {generated}</div>
          <div>Currency: {company?.currency || "USD"}</div>
        </div>
      </div>
    </div>
  );
}

export function SalesBySalesman({ data, currency }) {
  return (
    <div className="border border-border bg-card">
      <table className="w-full text-sm">
        <thead className="border-b border-border bg-muted/40">
          <tr className="text-left">
            <Th>Salesman</Th>
            <Th className="text-right">Commission %</Th>
            <Th className="text-right">Invoices</Th>
            <Th className="text-right">Sales</Th>
            <Th className="text-right pr-4">Commission</Th>
          </tr>
        </thead>
        <tbody>
          {data.rows.map((r, i) => (
            <tr key={r.salesman_id || `un-${i}`} className="border-b border-border last:border-b-0">
              <Td className={r.salesman_id ? "font-mono" : "font-mono text-muted-foreground italic"}>
                {r.name}
              </Td>
              <Td className="text-right num">{r.commission_pct}%</Td>
              <Td className="text-right num">{r.invoice_count}</Td>
              <Td className="text-right num">{fmtMoney(r.sales, currency)}</Td>
              <Td className="text-right num pr-4">{fmtMoney(r.commission, currency)}</Td>
            </tr>
          ))}
          <tr className="bg-muted/60 border-t-2 border-primary">
            <Td colSpan={3} className="font-serif-display text-lg">Totals</Td>
            <Td className="text-right num font-medium">{fmtMoney(data.total_sales, currency)}</Td>
            <Td className="text-right num font-medium pr-4">{fmtMoney(data.total_commission, currency)}</Td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}

const Th = ({ children, className = "" }) => (
  <th className={`px-4 py-3 overline ${className}`}>{children}</th>
);
const Td = ({ children, colSpan, className = "" }) => (
  <td colSpan={colSpan} className={`px-4 py-3 ${className}`}>{children}</td>
);
